import React, { useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, Spin, Table, Tag, Typography, message } from 'antd';
import { apiConfig } from '../../authConfig';
import { useResource } from '../../hooks/useResource';
import { formatDdMmYyyyTime } from '../../utils/supportApi';

const { Text, Paragraph, Title } = Typography;

const EVENTS_PAGE_SIZE = 25;
const PREVIEW_CELL_CHARS = 160;

/** Webhook events Pathkey has delivered to the caller's own tenant (GET /admin/pathkey-events: paginated,
 *  tenant-scoped by the server, never contains secrets). */
function PathkeyEvents({ api }) {
    const [page, setPage] = useState(1);
    const events = useResource(() => api.listPathkeyEvents({ page, pageSize: EVENTS_PAGE_SIZE }), [api, page]);

    if (events.status === 'loading' || events.status === 'idle') return <Spin />;
    if (events.status === 'error') {
        return (
            <Alert type="error" showIcon message="Could not load Pathkey events" description={events.error.message} action={<Button onClick={events.reload}>Retry</Button>} />
        );
    }
    if (events.data.total === 0) {
        return <Empty description="No Pathkey events received yet. Events appear here once Pathkey posts to the webhook URL above." />;
    }
    return (
        <Table
            rowKey={(r) => r.id}
            size="small"
            dataSource={events.data.events}
            scroll={{ x: true }}
            pagination={{
                current: events.data.page,
                pageSize: events.data.pageSize,
                total: events.data.total,
                showSizeChanger: false,
                onChange: (p) => setPage(p),
            }}
            columns={[
                { title: 'Received', dataIndex: 'receivedAt', render: (v) => formatDdMmYyyyTime(v) },
                { title: 'Pathkey event id', dataIndex: 'pathkeyEventId' },
                {
                    title: 'Processing',
                    dataIndex: 'processedAt',
                    render: (v) => (v ? <Tag color="green">Processed {formatDdMmYyyyTime(v)}</Tag> : <Tag>Stored, not yet processed</Tag>),
                },
                {
                    title: 'Payload preview',
                    render: (_, r) => (
                        <Text code title={r.payloadPreview}>
                            {r.payloadPreview.slice(0, PREVIEW_CELL_CHARS)}
                            {r.payloadPreview.length > PREVIEW_CELL_CHARS || r.payloadTruncated ? '…' : ''}
                        </Text>
                    ),
                },
            ]}
        />
    );
}

/**
 * Pathkey statutory-feed connection for the Lead Advisor's own firm tenant (drawer body).
 * `current` is the saved config or null; `onChanged` reloads it. The webhook secret is write-only: the API
 * never returns it and requires it (>= 16 chars) on every save. Re-saving does NOT re-activate an inactive
 * config unless `isActive: true` is sent, so Active is its own explicit control and is always sent.
 */
const PathkeyPanel = ({ api, current, onChanged }) => {
    const [accountId, setAccountId] = useState('');
    const [secret, setSecret] = useState('');
    const [active, setActive] = useState(true);
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);

    const shownAccountId = touched ? accountId : (current && current.pathkeyAccountId) || '';
    const shownActive = touched ? active : current ? current.isActive : true;
    const webhookUrl = current ? `${apiConfig.baseUrl}/api/v1/webhooks/pathkey/${current.tenantId}` : null;
    const secretOk = secret.trim().length >= 16;

    const save = async () => {
        setSaving(true);
        try {
            await api.savePathkeyConfig({
                ...(shownAccountId.trim() ? { pathkeyAccountId: shownAccountId.trim() } : {}),
                webhookSecret: secret,
                isActive: shownActive,
            });
            message.success('Pathkey configuration saved.');
            setSecret('');
            setTouched(false);
            await onChanged();
        } catch (err) {
            message.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <Paragraph type="secondary">
                Connect your firm to Pathkey's statutory and regulatory feed. Pathkey posts signed webhooks to the address below; each delivery is stored and listed under
                Received events.
            </Paragraph>

            {current ? (
                <div style={{ marginBottom: 16 }}>
                    <Text strong>Status: </Text>
                    {current.isActive ? <Tag color="green">Active</Tag> : <Tag>Inactive - incoming webhooks are rejected</Tag>}
                    <div><Text type="secondary">Last updated {formatDdMmYyyyTime(current.updatedAt)}</Text></div>
                    <div style={{ marginTop: 8 }}>
                        <Text strong>Webhook URL</Text>
                        <div><Text copyable code>{webhookUrl}</Text></div>
                    </div>
                </div>
            ) : (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Pathkey is not configured for your firm yet." />
            )}

            <div style={{ display: 'grid', gap: 12 }}>
                <div>
                    <Text strong>Pathkey account id (optional)</Text>
                    <Input
                        aria-label="Pathkey account id"
                        value={shownAccountId}
                        onChange={(e) => { setTouched(true); setAccountId(e.target.value); setActive(shownActive); }}
                    />
                </div>
                <div>
                    <Text strong>Webhook secret</Text>
                    <Input
                        aria-label="Webhook secret"
                        type="password"
                        autoComplete="new-password"
                        placeholder="At least 16 characters - required on every save, never shown again"
                        value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                    />
                </div>
                <Checkbox
                    checked={shownActive}
                    onChange={(e) => { setTouched(true); setAccountId(shownAccountId); setActive(e.target.checked); }}
                >
                    Active (accept incoming webhooks)
                </Checkbox>
                <div>
                    <Button type="primary" loading={saving} disabled={!secretOk} onClick={save}>
                        {current ? 'Save changes' : 'Connect Pathkey'}
                    </Button>
                </div>
            </div>

            <Title level={5} style={{ marginTop: 24 }}>Received events</Title>
            <PathkeyEvents api={api} />
        </div>
    );
};

export default PathkeyPanel;
